<template>
  <ModalForm
    v-model="isModalOpen"
    :data="store.personalDetails ?? {}"
    :title="$t('pages.onboarding.sole_trader.edit_personal.title')"
    :on-submit="handleForm"
    disable-submit-on-error>
    <p class="subheader">
      {{ $t('pages.onboarding.sole_trader.edit_personal.title') }}
    </p>
    <div class="grid grid-cols-2 gap-24">
      <FormKit
        type="TextPatternInput"
        name="firstName"
        :label="$t('form.label.first_name')"
        :pattern="shortNameRule.pattern"
        :validation="shortNameRule.validation"/>
      <FormKit
        type="TextPatternInput"
        name="lastName"
        :label="$t('form.label.last_name')"
        :pattern="shortNameRule.pattern"
        :validation="shortNameRule.validation"/>
      <FormKit
        type="CountryDropdown"
        name="nationalityIso3"
        :label="$t('form.label.nationality')"
        value-key="shortName"
        :options="ALL_COUNTRIES"
        validation="required"/>
      <FormKit
        type="DateInput"
        name="dateOfBirth"
        :label="$t('form.label.date_of_birth')"
        :validation="`required|date_before:${new Date(time.eighteenYearsAgo)}`"/>
    </div>

    <p class="subheader">
      {{ $t('pages.onboarding.sole_trader.edit_personal.subtitle.residential_address') }}
    </p>
    <FormKit
      type="group"
      name="residentialAddress">
      <div class="grid grid-cols-2 gap-24">
        <AddressFields
          :country-iso3="{
            label: $t('form.label.country_of_residence'),
          }"/>
      </div>
    </FormKit>

    <p class="subheader">
      {{ $t('pages.onboarding.sole_trader.edit_personal.subtitle.mailing_address') }}
    </p>
    <FormKit
      v-model="isAddressesSame"
      type="checkbox"
      :label="$t('pages.onboarding.business.company_details.address.data.same_address')"
      name="isAddressesSame"
      @input="resetMailingAddress"/>
    <FormKit
      id="mailingAddress"
      type="group"
      name="mailingAddress">
      <div class="grid grid-cols-2 gap-24">
        <AddressFields v-if="!isAddressesSame"/>
      </div>
    </FormKit>
  </ModalForm>
</template>

<script setup lang="ts">
import { reset } from '@formkit/core';
import { nextTick } from 'vue';

import ModalForm from '@/components/modals/ModalForm.vue';
import { ALL_COUNTRIES } from '@/constants/countries';
import AddressFields from '@/modules/onboarding/components/AddressFields.vue';
import { useDoubleOnboardingAddressForm } from '@/modules/onboarding/utils';
import { NameFormKitRule } from '@/rules/formkit-rules/Name';
import { Onboarding } from '@/services/requests/onboarding/Onboarding.types';
import {
  useOnboardingAdminSoleTraderStore,
} from '@/stores/onboarding/OnboardingAdminSoleTrader';
import { time } from '@/utils/time';

const isModalOpen = defineModel<boolean>('isModalOpen', { required: true });

const store = useOnboardingAdminSoleTraderStore();

const {
  isAddressesSame,
  resetSecondAddress: resetMailingAddress,
} = useDoubleOnboardingAddressForm(
  (address) => nextTick(() => reset('mailingAddress', address ?? {})),
  store.personalDetails?.residentialAddress,
  store.personalDetails?.mailingAddress,
);

const handleForm = async (data: Onboarding.SoleTrader.Details) => {
  if (isAddressesSame.value) {
    data.mailingAddress = data.residentialAddress;
  }

  await store.updatePersonalDetails(data, {
    onSuccess: () => {
      isModalOpen.value = false;
    },
  });
};

const shortNameRule = NameFormKitRule(50);
</script>

<style lang="scss" scoped>
.subheader {
  @apply font-semibold text-22;
}
</style>
